import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import SEO from '../components/SEO';
import plansData from '../config/plans';
import { capture } from '../analytics';
import '../styles.css';

function planTierFromSubscription(subscription) {
  const value = String(subscription?.planName || '').toLowerCase();
  if (value.includes('studio')) return 'studio';
  if (value.includes('pro')) return 'pro';
  if (value.includes('starter')) return 'starter';
  return null;
}

function PricingPage({ subscription = null }) {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const hasActiveSubscription = ['active', 'trialing'].includes(
    subscription?.status,
  );
  const hasUsedTrial =
    subscription?.trialAvailable === false ||
    ['granted', 'denied'].includes(subscription?.trialEligibilityStatus);
  const currentPlan = planTierFromSubscription(subscription);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="page-container pricing-page-shell">
      <SEO 
        title="Virgil Pricing | 14-Day Public Beta Trial"
        description="Compare Virgil Starter, Pro, and Studio. Start with a card-backed 14-day trial that includes up to $20 of AI usage and no trial overage."
        keywords="Virgil pricing, AI creative writing pricing, screenwriting AI cost, AI writer plans"
      />
      {/* Main Content */}
      <section className="content-section pricing-section pricing-page-section static-page-section">
        <h1 className="page-title page-title--wide pricing-page-title">
          {hasActiveSubscription ? 'Change your plan' : 'Choose your plan'}
        </h1>
        <p className="page-lead page-lead--compact pricing-page-subtitle">
          {hasActiveSubscription
            ? 'Choose the plan that fits your current work. Changes take effect immediately and are prorated by Stripe.'
            : hasUsedTrial
              ? 'Welcome back. Choose a paid monthly plan to reactivate your Virgil access.'
              : 'Trial AI access lasts up to 14 days or $20 of included usage, whichever comes first. A card is required. There is no trial overage.'}
        </p>

        <div className="chip-container pricing-page-chips">
          <div className="chip">100 public-beta seats</div>
          <div className="chip">Switch models any time</div>
        </div>
        
        <div className="pricing-grid pricing-page-grid">
          {plansData.map((plan) => {
            const isCurrentPlan = hasActiveSubscription && currentPlan === plan.id;
            return (
            <div key={plan.id} className={`pricing-card pricing-page-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current' : ''}`}>
              <div className="pricing-plan-name pricing-page-plan-name">
                {plan.name}
                {plan.popular && <span className="popular-badge">Most popular</span>}
              </div>
              <div className="pricing-plan-price pricing-page-price">
                <span className="pricing-price-amount">${plan.monthlyPrice}</span>
                <span className="pricing-price-period">/month</span>
              </div>
              <div className="pricing-plan-description pricing-page-description">{plan.description}</div>
              <button 
                disabled={isCurrentPlan}
                onClick={() => {
                  const returnTo = `/checkout?plan=${encodeURIComponent(plan.id)}`;
                  if (isAuthenticated) {
                    navigate(returnTo);
                  } else {
                    capture('signup_started');
                    loginWithRedirect({
                      appState: { returnTo },
                      authorizationParams: { screen_hint: 'signup', prompt: 'select_account' }
                    });
                  }
                }} 
                className="cta-button pricing-card-cta"
              >
                {isCurrentPlan
                  ? 'Current plan'
                  : hasActiveSubscription
                    ? `Switch to ${plan.name}`
                    : hasUsedTrial
                      ? `Choose ${plan.name}`
                      : 'Start your 14-day trial'}
              </button>
            </div>
          );})}
        </div>

        <div className="pricing-page-detail-grid">
          <div className="pricing-billing-card pricing-page-billing">
            <h3 className="billing-title">
              {hasActiveSubscription ? 'How plan changes work' : 'How billing works'}
            </h3>
            <ul className="billing-list">
              {hasActiveSubscription ? (
                <>
                  <li>Your selected plan becomes active immediately</li>
                  <li>Stripe prorates the plan difference on your next invoice</li>
                  <li>Your billing date stays the same</li>
                  <li>Your existing trial end date is preserved if you are still trialing</li>
                </>
              ) : hasUsedTrial ? (
                <>
                  <li>Your one-time free trial will not restart</li>
                  <li>Your selected paid plan starts after Stripe checkout</li>
                  <li>Paid plans include monthly AI credit; additional usage is metered</li>
                  <li>You can change or cancel from your account</li>
                </>
              ) : (
                <>
                  <li>Your 14-day trial starts after Stripe accepts your card</li>
                  <li>Trial AI access stops after 14 days or $20 of usage, whichever comes first</li>
                  <li>AI usage hard-stops at the trial limit; there is no trial overage</li>
                  <li>Your selected monthly plan starts automatically after Stripe's 14-day trial</li>
                  <li>Paid plans include monthly AI credit; additional paid usage is metered</li>
                  <li>Cancel before conversion to avoid the first monthly charge</li>
                </>
              )}
            </ul>
          </div>

          <div className="pricing-features pricing-page-features">
            <h3>All Plans Include</h3>
            <ul className="features-list pricing-page-feature-list">
              <li>Screenplay editor with professional formatting</li>
              <li>Access to all AI models (GPT, Claude, Gemini, and Grok)</li>
              <li>Script coverage generation</li>
              <li>Autonomous agent mode</li>
              <li>Project saving and management</li>
              <li>Regular updates and new features</li>
              <li>Image and video generation tools</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PricingPage;
